// 2x2 matrices are [a, b, c, d], mapping (x, y) to (a*x + b*y, c*x + d*y).

const GRAPHIC = 6;
const TEXTURE = 1;
const GRAPHICGROUP = 7;
const TEXT = 9;
const ANIMATIONGROUP = 11;

export const transformPoint = (m, point) => ({x: m[0] * point.x + m[1] * point.y, y: m[2] * point.x + m[3] * point.y});

// the same transform seen from a child rotated by `rotation` inside the frame
export const toChildFrame = (m, rotation) => {
	const cos = Math.cos(rotation);
	const sin = Math.sin(rotation);
	const a = m[0] * cos + m[1] * sin;
	const b = -m[0] * sin + m[1] * cos;
	const c = m[2] * cos + m[3] * sin;
	const d = -m[2] * sin + m[3] * cos;
	return [cos * a + sin * c, cos * b + sin * d, -sin * a + cos * c, -sin * b + cos * d];
}

// mirrored groups render with scale.x = -1
export const toMirroredFrame = m => [m[0], -m[1], -m[2], m[3]];

// sprites can't skew, so they take how much each of their axes stretches
export const axisScales = m => [Math.hypot(m[0], m[2]), Math.hypot(m[1], m[3])];

export const radiusScale = m => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));

export const transformVertices = (vertices, m) => {
	vertices.flat(10).forEach(vertex => {
		for (const point of [vertex, vertex.point1, vertex.point2]) {
			if (!point) continue;
			const {x, y} = transformPoint(m, point);
			point.x = x;
			point.y = y;
		}
	});
}

const parseList = list => list.map(object => typeof object === 'string' ? JSON.parse(object) : object);

// Serialized group children, positions relative to the group origin.
export const transformGraphicObjects = (graphicObjects, m) => {
	parseList(graphicObjects).forEach((object, i) => {
		graphicObjects[i] = object;
		const position = transformPoint(m, {x: object[1], y: object[2]});
		object[1] = position.x;
		object[2] = position.y;
		const local = toChildFrame(m, object[3] || 0);
		if (object[0] === GRAPHIC) {
			transformVertices(object[11], local);
			if (object[10]) object[10] *= radiusScale(local);
		} else if (object[0] === TEXTURE) {
			const [scaleX, scaleY] = axisScales(local);
			object[14] = (object[14] || 1) * scaleX;
			object[15] = (object[15] || 1) * scaleY;
		} else if (object[0] === TEXT) {
			object[10] *= radiusScale(local);
		} else if (object[0] === GRAPHICGROUP) {
			transformGraphicObjects(object[7], object[17] ? toMirroredFrame(local) : local);
		}
	});
}

// Groups made before textureScale render grouped textures at their original size, flipped by the sign only.
export const normalizeGroupedTextureScale = graphicObjects => {
	parseList(graphicObjects).forEach((object, i) => {
		graphicObjects[i] = object;
		if (object[0] === TEXTURE) {
			object[14] = Math.sign(object[14] || 1);
			object[15] = Math.sign(object[15] || 1);
		} else if (object[0] === GRAPHICGROUP) {
			normalizeGroupedTextureScale(object[7]);
		}
	});
}

export const hasCircleGraphic = graphicObjects => parseList(graphicObjects).some(object =>
	(object[0] === GRAPHIC && object[10]) || (object[0] === GRAPHICGROUP && hasCircleGraphic(object[7])));

// switch a group to rendering its textures at their stored scale without changing how it looks
export const useGroupedTextureScale = data => {
	if (data.textureScale) return;
	normalizeGroupedTextureScale(data.graphicObjects);
	data.textureScale = true;
}

// matrix is in the graphic's own frame
export const scaleGraphicData = (data, m) => {
	if (data.type === GRAPHICGROUP || data.type === ANIMATIONGROUP) {
		useGroupedTextureScale(data);
		transformGraphicObjects(data.graphicObjects, data.mirrored ? toMirroredFrame(m) : m);
	} else if (data.type === GRAPHIC) {
		transformVertices(data.vertices, m);
		if (data.radius) data.radius *= radiusScale(m);
	} else if (data.type === TEXTURE) {
		const [scaleX, scaleY] = axisScales(m);
		data.scaleX *= scaleX;
		data.scaleY *= scaleY;
	} else if (data.type === TEXT) {
		data.fontSize *= radiusScale(m);
	}
}

// matrix is in the body's frame; the graphic sits at an offset and rotation from it
export const scaleAttachedGraphicData = (data, m) => {
	const offset = transformPoint(m, {
		x: data.texturePositionOffsetLength * Math.cos(data.texturePositionOffsetAngle),
		y: -data.texturePositionOffsetLength * Math.sin(data.texturePositionOffsetAngle),
	});
	data.texturePositionOffsetLength = Math.hypot(offset.x, offset.y);
	data.texturePositionOffsetAngle = Math.atan2(-offset.y, offset.x);
	scaleGraphicData(data, toChildFrame(m, -data.textureAngleOffset));
}

export const scaleBodyData = (data, m) => {
	transformVertices(data.vertices, m);
	const scale = radiusScale(m);
	data.radius = Array.isArray(data.radius) ? data.radius.map(radius => radius * scale) : data.radius * scale;
}

// Mirroring flips along the object's own x (horizontal) or y axis. A child rotated by r inside the frame
// ends up rotated by -r and flipped along the same axis of its own frame.
export const mirrorVertices = (vertices, horizontal) => {
	vertices.flat(10).forEach(vertex => {
		for (const point of [vertex, vertex.point1, vertex.point2]) {
			if (!point) continue;
			if (horizontal) point.x = -point.x;
			else point.y = -point.y;
		}
	});
}

const mirrorGraphicObjects = (graphicObjects, horizontal) => {
	parseList(graphicObjects).forEach((object, i) => {
		graphicObjects[i] = object;
		if (horizontal) object[1] = -object[1];
		else object[2] = -object[2];
		object[3] = -object[3];
		if (object[0] === GRAPHIC) {
			mirrorVertices(object[11], horizontal);
		} else if (object[0] === TEXTURE) {
			if (horizontal) object[14] = -(object[14] || 1);
			else object[15] = -(object[15] || 1);
		} else if (object[0] === GRAPHICGROUP) {
			mirrorGraphicObjects(object[7], horizontal);
		}
	});
}

export const mirrorGraphicData = (data, horizontal) => {
	if (data.type === GRAPHICGROUP || data.type === ANIMATIONGROUP) {
		mirrorGraphicObjects(data.graphicObjects, horizontal);
	} else if (data.type === GRAPHIC) {
		mirrorVertices(data.vertices, horizontal);
	} else if (data.type === TEXTURE) {
		if (horizontal) data.scaleX = -data.scaleX;
		else data.scaleY = -data.scaleY;
	}
}

export const mirrorAttachedGraphicData = (data, horizontal) => {
	let x = Math.cos(data.texturePositionOffsetAngle);
	let y = Math.sin(data.texturePositionOffsetAngle);
	if (horizontal) x = -x;
	else y = -y;
	data.texturePositionOffsetAngle = Math.atan2(y, x);
	data.textureAngleOffset = -data.textureAngleOffset;
	mirrorGraphicData(data, horizontal);
}

export const mirrorBodyData = (data, horizontal) => mirrorVertices(data.vertices, horizontal);
